if (!customElements.get('affiliate-apply-form')) {
  customElements.define(
    'affiliate-apply-form',
    class AffiliateApplyForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.button = this.querySelector('[type="submit"]');
        this.status = this.querySelector('[data-form-status]');
        this.success = this.querySelector('[data-form-success]');
        this.successText = this.querySelector('[data-form-success-text]');
        this.note = this.querySelector('[data-form-note]');
        this.tokenInput = this.form && this.form.querySelector('input[name="token"]');
        if (!this.form) return;

        this.onSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        if (this.form && this.onSubmit) {
          this.form.removeEventListener('submit', this.onSubmit);
        }
      }

      setStatus(message, isError) {
        if (!this.status) return;
        this.status.hidden = !message;
        this.status.textContent = message || '';
        this.status.classList.toggle('is-error', Boolean(isError));
      }

      showSuccess() {
        this.form.hidden = true;
        if (this.note) this.note.hidden = true;
        this.setStatus('');
        this.classList.add('is-success');
        if (this.successText) {
          this.successText.textContent =
            this.dataset.successMessage || 'Application submitted. Check your email for next steps.';
        }
        if (this.success) this.success.hidden = false;
      }

      async getFreshToken() {
        const formPage = this.dataset.formPage;
        if (!formPage) return '';

        const response = await fetch(formPage, {
          headers: { Accept: 'text/html' },
        });
        const html = await response.text();
        const match = html.match(/token:\s*["']([0-9a-f-]{36})["']/i);
        return match ? match[1] : '';
      }

      parseResponse(raw) {
        try {
          return JSON.parse(raw);
        } catch (error) {
          return null;
        }
      }

      isSuccess(response, payload, raw) {
        if (payload && payload.success === false) return false;
        if (!response.ok) return false;
        if (payload && typeof payload.message === 'string' && /session has expired/i.test(payload.message)) {
          return false;
        }
        if (/session has expired/i.test(raw)) return false;
        if (payload && payload.success === true) return true;
        if (/content-thanks|text-thanks/i.test(raw)) return true;
        return Boolean(payload) || response.ok;
      }

      errorMessage(payload, raw) {
        const errors = payload && ((payload.data && payload.data.errors) || payload.errors);
        if (payload && payload.message) return payload.message;
        if (errors) return Object.values(errors).flat().join(' ');
        if (/session has expired/i.test(raw)) {
          return 'Your session has expired. Please reload the page and try again.';
        }
        return 'Something went wrong. Please try again.';
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (!this.form.reportValidity()) return;

        const endpoint = this.dataset.endpoint;
        if (!endpoint) return;

        const originalLabel = this.button ? this.button.textContent : '';
        if (this.button) this.button.disabled = true;
        this.setStatus('');

        try {
          const token = await this.getFreshToken();
          if (!token) {
            this.setStatus('Could not start the application. Please reload the page and try again.', true);
            return;
          }
          if (this.tokenInput) this.tokenInput.value = token;

          const body = new URLSearchParams();
          for (const [key, value] of new FormData(this.form).entries()) {
            body.append(key, value);
          }

          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              Accept: 'application/json, text/html',
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body,
          });

          const raw = await response.text();
          const payload = this.parseResponse(raw);

          if (!this.isSuccess(response, payload, raw)) {
            this.setStatus(this.errorMessage(payload, raw), true);
            return;
          }

          this.showSuccess();
        } catch (error) {
          this.setStatus('Something went wrong. Please try again.', true);
        } finally {
          if (this.button) {
            this.button.disabled = false;
            this.button.textContent = originalLabel;
          }
        }
      }
    }
  );
}
